import { useState, useCallback } from "react";
import { useApi } from "../../../hooks/useApi.js";
import type { Partner, PartnersListResponse } from "@kappa-v2/contracts";

export interface PartnerFilters {
  active?: string; // "true" | "false" | ""
}

export function usePartners() {
  const { call } = useApi();
  const [rows, setRows] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(
    async (filters: PartnerFilters = {}) => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (filters.active) params.set("active", filters.active);
        const qs = params.toString() ? `?${params}` : "";
        const res = await call<PartnersListResponse>(`/api/v1/partners${qs}`);
        setRows(res.data ?? []);
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : "Errore caricamento soci";
        setError(msg);
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [call]
  );

  const save = useCallback(
    async (data: Partial<Partner>, id?: string): Promise<Partner> => {
      setSaving(true);
      try {
        if (id) {
          return await call<Partner>(`/api/v1/partners/${id}`, {
            method: "PUT",
            body: { ...data, _id: id }
          });
        }
        return await call<Partner>("/api/v1/partners", { method: "POST", body: data });
      } finally {
        setSaving(false);
      }
    },
    [call]
  );

  const remove = useCallback(
    async (id: string): Promise<void> => {
      await call(`/api/v1/partners/${id}`, { method: "DELETE" });
    },
    [call]
  );

  const toggleActive = useCallback(
    async (partner: Partner): Promise<void> => {
      await call(`/api/v1/partners/${partner._id}`, {
        method: "PATCH",
        body: { _id: partner._id, active: !partner.active }
      });
    },
    [call]
  );

  return { rows, loading, saving, error, fetchAll, save, remove, toggleActive };
}
